import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Link,
  Spinner,
  Text,
  useToast,
  Container,
  Flex,
  Button,
  Icon,
  Progress,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  Collapse,
  IconButton,
  VStack,
  Divider,
  Code,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon, ChevronDownIcon, ChevronRightIcon, TriangleUpIcon, TriangleDownIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import { normalizeAnalysisText } from './NMAClaims/shared';

const JobsDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  // Advanced Filtering State (See features/jobs-dashboard-advanced-filtering.md)
  const getDefaultStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  };
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('');
  const [showFailed, setShowFailed] = useState(false);
  
  // Sorting State
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Expandable Rows State
  const [expandedRows, setExpandedRows] = useState({});

  // Payer Data Analysis Report Modal State
  const { isOpen: isReportOpen, onOpen: onOpenReport, onClose: onCloseReport } = useDisclosure();
  const [selectedReportJob, setSelectedReportJob] = useState(null);
  const [reportAnalysis, setReportAnalysis] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const handleOpenJobReport = async (job) => {
    setSelectedReportJob(job);
    setReportAnalysis(null);
    setIsLoadingReport(true);
    onOpenReport();
    try {
      let stepResult = null;
      if (job.execution_history && job.execution_history.length > 0) {
        for (let i = job.execution_history.length - 1; i >= 0; i--) {
          if (job.execution_history[i].step_result) {
            stepResult = job.execution_history[i].step_result;
            break;
          }
        }
      }

      const analysisId = stepResult?.analysis_id || stepResult?.insight_id;
      const endpoint = analysisId
        ? `/api/payer-data-analysis/${analysisId}`
        : `/api/payer-data-analysis?job_id=${encodeURIComponent(job.id)}`;

      const res = await callApi(endpoint, accessToken);
      if (res.ok) {
        const data = await res.json();
        if (data.payer_data_analysis) {
          setReportAnalysis(data.payer_data_analysis);
          return;
        }
        if (data.analyses && data.analyses.length > 0) {
          setReportAnalysis(data.analyses[0]);
          return;
        }
      }

      if (stepResult?.report_markdown || stepResult?.analysis_result) {
        setReportAnalysis({
          content_type: job.payload?.content_type || job.job_type,
          target_key: job.payload?.target_key || job.id,
          analysis_result: stepResult.analysis_result || stepResult,
        });
      }
    } catch (err) {
      console.error('Error fetching job analysis report:', err);
    } finally {
      setIsLoadingReport(false);
    }
  };

  const fetchJobs = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '0'); // Fetch all rows
      
      if (startDate) {
        params.append('start_date', new Date(startDate).toISOString());
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.append('end_date', end.toISOString());
      }
      if (selectedJobType) {
        params.append('job_type', selectedJobType);
      }

      const response = await callApi(`/api/jobs?${params.toString()}`, accessToken);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({
        title: 'Error fetching jobs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchJobs();
    }
  }, [accessToken, startDate, endDate, selectedJobType]);

  // Added local timer to re-render progress bars periodically
  // per USN Josh call Sept 4 (See features/jobs-dashboard-enhancements.md)
  const [currentTime, setCurrentTime] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getProcessingStartTime = (job) => {
    // Helper to ensure UTC parsing since backend isoformat() might lack the 'Z'
    const parseUtcDate = (dateStr) => {
      if (!dateStr) return Date.now();
      return new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`).getTime();
    };

    if (job.execution_history && job.execution_history.length > 0) {
      const claimedStep = job.execution_history.find(step => step.action === 'claimed' || step.status_change === 'processing');
      if (claimedStep && claimedStep.timestamp) {
        return parseUtcDate(claimedStep.timestamp);
      }
    }
    return parseUtcDate(job.updated_at || job.created_at);
  };

  const getJobProgress = (job) => {
    if (job.status !== 'processing') return null;
    
    const startTime = getProcessingStartTime(job);
    const elapsedMinutes = (currentTime - startTime) / (1000 * 60);
    
    // Assume 10 minutes expected duration
    const expectedDurationMinutes = 10;
    let progress = (elapsedMinutes / expectedDurationMinutes) * 100;
    
    // Handle clock skew (negative elapsed time) and cap at 99%
    if (progress < 0 || isNaN(progress)) progress = 0;
    if (progress > 99) progress = 99;
    
    return {
      progress,
      elapsedMinutes,
      isTimeout: false // Removed per NMA Teams meeting (See features/jobs-dashboard-nma-customization.md)
    };
  };

  const getStatusBadge = (status) => {

    const colorScheme = {
      pending: 'purple',
      processing: 'orange',
      completed: 'green',
      failed: 'red'
    }[status] || 'gray';

    return <Badge colorScheme={colorScheme}>{status.toUpperCase()}</Badge>;
  };

  const getResultUrl = (job) => {
    // Check execution history for a result URL (from the worker)
    if (job.execution_history && job.execution_history.length > 0) {
      // Look backwards through history for the most recent result
      for (let i = job.execution_history.length - 1; i >= 0; i--) {
        const step = job.execution_history[i];
        if (step.step_result && step.step_result.result_url) {
          return step.step_result.result_url;
        }
      }
    }
    return null;
  };

  // Extract unique job types for the filter dropdown
  const uniqueJobTypes = useMemo(() => {
    const types = new Set(jobs.map(job => job.job_type));
    return Array.from(types).sort();
  }, [jobs]);

  // Sorting Logic
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedJobs = useMemo(() => {
    const filtered = showFailed ? jobs : jobs.filter(job => job.status !== 'failed');
    const sorted = [...filtered].sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === 'owner') {
        valA = a.user_email || '';
        valB = b.user_email || '';
      } else if (sortColumn === 'case_id') {
        valA = a.display_case_id || '';
        valB = b.display_case_id || '';
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [jobs, sortColumn, sortDirection, showFailed]);

  const toggleRow = (jobId) => {
    setExpandedRows(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <TriangleUpIcon ml={1} w={3} h={3} /> : <TriangleDownIcon ml={1} w={3} h={3} />;
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Heading size="lg">Background Jobs</Heading>
          <Button 
            leftIcon={<RepeatIcon />} 
            onClick={fetchJobs} 
            isLoading={isLoading}
            colorScheme="blue"
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* Advanced Filters */}
        <Box bg="white" p={4} mb={6} shadow="sm" borderRadius="md" borderWidth="1px">
          <HStack spacing={6} align="flex-end">
            <FormControl maxW="200px">
              <FormLabel fontSize="sm" mb={1}>Start Date</FormLabel>
              <Input 
                type="date" 
                size="sm" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
            </FormControl>
            <FormControl maxW="200px">
              <FormLabel fontSize="sm" mb={1}>End Date</FormLabel>
              <Input 
                type="date" 
                size="sm" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
              />
            </FormControl>
            <FormControl maxW="250px">
              <FormLabel fontSize="sm" mb={1}>Job Type</FormLabel>
              <Select 
                size="sm" 
                placeholder="All Types" 
                value={selectedJobType} 
                onChange={(e) => setSelectedJobType(e.target.value)}
              >
                {uniqueJobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
            </FormControl>
            <Button size="sm" onClick={() => { setStartDate(getDefaultStartDate()); setEndDate(''); setSelectedJobType(''); }}>
              Clear Filters
            </Button>
            <Checkbox 
              size="sm" 
              isChecked={showFailed} 
              onChange={(e) => setShowFailed(e.target.checked)}
              ml={4}
            >
              Show Failed
            </Checkbox>
          </HStack>
        </Box>

        <Box bg="white" shadow="sm" borderRadius="md" overflow="hidden" borderWidth="1px">
          {isLoading && jobs.length === 0 ? (
            <Flex justify="center" align="center" p={10}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : jobs.length === 0 ? (
            <Box p={10} textAlign="center">
              <Text color="gray.500">No background jobs found matching criteria.</Text>
            </Box>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th w="40px"></Th>
                  <Th cursor="pointer" onClick={() => handleSort('id')}>Job ID <SortIcon column="id" /></Th>
                  <Th cursor="pointer" onClick={() => handleSort('job_type')}>Type <SortIcon column="job_type" /></Th>
                  <Th cursor="pointer" onClick={() => handleSort('owner')}>Owner <SortIcon column="owner" /></Th>
                  <Th cursor="pointer" onClick={() => handleSort('status')}>Status <SortIcon column="status" /></Th>
                  <Th cursor="pointer" onClick={() => handleSort('created_at')}>Created <SortIcon column="created_at" /></Th>
                  <Th cursor="pointer" onClick={() => handleSort('case_id')}>Case ID <SortIcon column="case_id" /></Th>
                  <Th>DOS</Th>
                  <Th>Payer</Th>
                  <Th>Result</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedJobs.map((job) => {
                  const resultUrl = getResultUrl(job);
                  const displayCaseId =
                    job.display_case_id ||
                    job.payload?.target_key ||
                    'N/A';
                  const dos = job.payload?.date_of_surgery || '-';
                  const payer =
                    job.payload?.payer_type ||
                    job.payload?.entity_refs?.payer_name ||
                    job.payload?.source_snapshot?.payer_name ||
                    '-';
                  const progressData = getJobProgress(job);
                  const isExpanded = !!expandedRows[job.id];
                  const hideProgressBar = job.job_type === 'nma_coding';
                  const hasAnalysisReport =
                    job.status === 'completed' &&
                    (job.job_type === 'payer_data_analysis' ||
                      job.job_type === 'nma_appeal_letter' ||
                      (job.execution_history || []).some(
                        (s) =>
                          s.step_result?.analysis_id ||
                          s.step_result?.report_markdown ||
                          s.step_result?.analysis_result
                      ));
                  
                  // Mask internal emails per USN Josh call Sept 4
                  let displayEmail = job.user_email || 'Unknown';
                  if (displayEmail.includes('@revedy.io')) {
                    displayEmail = 'admin';
                  }
                  
                  return (
                    <React.Fragment key={job.id}>
                      <Tr _hover={{ bg: "gray.50" }}>
                        <Td>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            onClick={() => toggleRow(job.id)}
                            aria-label="Toggle Details"
                          />
                        </Td>
                        <Td fontSize="sm" fontFamily="monospace">
                          {job.id.substring(0, 8)}...
                        </Td>
                        <Td fontWeight="medium">{job.job_type}</Td>
                        <Td fontSize="sm">{displayEmail}</Td>
                        <Td>
                          <Box>
                            {getStatusBadge(job.status)}
                            {job.status === 'processing' && !hideProgressBar && (
                              <Progress 
                                value={progressData.progress} 
                                size="xs" 
                                colorScheme="orange" 
                                mt={2} 
                                borderRadius="md"
                                hasStripe
                                isAnimated
                              />
                            )}
                          </Box>
                        </Td>
                        <Td>{new Date(job.created_at).toLocaleString()}</Td>
                        <Td maxW="220px" isTruncated title={displayCaseId}>{displayCaseId}</Td>
                        <Td>{dos}</Td>
                        <Td>{payer}</Td>
                        <Td>
                          {hasAnalysisReport ? (
                            <Button
                              size="xs"
                              colorScheme="purple"
                              onClick={() => handleOpenJobReport(job)}
                            >
                              View Report
                            </Button>
                          ) : resultUrl ? (
                            <Link href={resultUrl} isExternal color="blue.500" fontWeight="medium">
                              View Result <Icon as={ExternalLinkIcon} mx="2px" />
                            </Link>
                          ) : job.status === 'failed' ? (
                            <Text color="red.500" fontSize="sm">
                              {job.execution_history?.[job.execution_history.length - 1]?.error || 'Failed'}
                            </Text>
                          ) : (
                            <Text color="gray.400" fontSize="sm">-</Text>
                          )}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td colSpan={10} p={0} borderBottom={isExpanded ? "1px solid" : "none"} borderColor="gray.200">
                          <Collapse in={isExpanded} animateOpacity>
                            <Box p={4} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
                              <Flex gap={6}>
                                <Box flex="1">
                                  <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.600">Payload</Text>
                                  <Code display="block" whiteSpace="pre-wrap" p={3} borderRadius="md" fontSize="xs" bg="white" borderWidth="1px">
                                    {JSON.stringify(job.payload, null, 2)}
                                  </Code>
                                </Box>
                                <Box flex="1">
                                  <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.600">Execution History</Text>
                                  <VStack align="stretch" spacing={2} bg="white" p={3} borderRadius="md" borderWidth="1px" maxH="300px" overflowY="auto">
                                    {job.execution_history && job.execution_history.length > 0 ? (
                                      job.execution_history.map((step, idx) => (
                                        <Box key={idx} fontSize="xs">
                                          <Text color="gray.500" mb={1}>{new Date(step.timestamp).toLocaleString()}</Text>
                                          <Code display="block" whiteSpace="pre-wrap" p={2} bg="gray.50">
                                            {JSON.stringify(step, null, 2)}
                                          </Code>
                                          {idx < job.execution_history.length - 1 && <Divider mt={2} />}
                                        </Box>
                                      ))
                                    ) : (
                                      <Text fontSize="sm" color="gray.500">No execution history available.</Text>
                                    )}
                                  </VStack>
                                </Box>
                              </Flex>
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Payer Data Analysis Report Modal */}
        <Modal isOpen={isReportOpen} onClose={onCloseReport} size="4xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader bg="purple.800" color="white" borderTopRadius="md">
              {normalizeAnalysisText(
                reportAnalysis?.analysis_result?.headline ||
                  selectedReportJob?.payload?.title ||
                  `Analysis Report (${selectedReportJob?.payload?.target_key || selectedReportJob?.id?.slice(0, 8)})`
              )}
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={5} bg="gray.50">
              {isLoadingReport ? (
                <Flex justify="center" py={10}>
                  <Spinner size="xl" color="purple.500" />
                </Flex>
              ) : !reportAnalysis ? (
                <Text fontSize="sm" color="gray.600">
                  No PayerDataAnalysis report record found for this job yet.
                </Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {reportAnalysis.analysis_result?.research_description && (
                    <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                      <Text fontSize="xs" fontWeight="bold" color="blue.800" textTransform="uppercase" mb={1}>
                        Research Description
                      </Text>
                      <Box fontSize="sm" color="gray.800" sx={{ '& p': { mb: 1.5 }, '& ul, & ol': { pl: 5, mb: 1.5 } }}>
                        <ReactMarkdown>{normalizeAnalysisText(reportAnalysis.analysis_result.research_description)}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                  {(reportAnalysis.analysis_result?.executive_summary || reportAnalysis.analysis_result?.root_cause_summary) && (
                    <Box p={3} bg="white" borderRadius="md" borderWidth="1px">
                      <Text fontSize="xs" fontWeight="bold" color="purple.700" textTransform="uppercase" mb={1}>
                        Executive Summary
                      </Text>
                      <Box fontSize="sm" color="gray.800" sx={{ '& p': { mb: 1.5 }, '& ul, & ol': { pl: 5, mb: 1.5 } }}>
                        <ReactMarkdown>
                          {normalizeAnalysisText(
                            reportAnalysis.analysis_result.executive_summary ||
                              reportAnalysis.analysis_result.root_cause_summary
                          )}
                        </ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                  {reportAnalysis.analysis_result?.recommendation && (
                    <Box p={3} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                      <Text fontSize="xs" fontWeight="bold" color="green.800" textTransform="uppercase" mb={1}>
                        Recommendations: Rules &amp; Processes
                      </Text>
                      <Box fontSize="sm" color="gray.800" sx={{ '& p': { mb: 1.5 }, '& ul, & ol': { pl: 5, mb: 1.5 } }}>
                        <ReactMarkdown>{normalizeAnalysisText(reportAnalysis.analysis_result.recommendation)}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                  {(reportAnalysis.analysis_result?.evidence_markdown || reportAnalysis.analysis_result?.report_markdown) && (
                    <Box
                      p={4}
                      bg="white"
                      borderRadius="md"
                      borderWidth="1px"
                      fontSize="sm"
                      sx={{
                        'h1, h2, h3': { fontWeight: 'bold', mt: 3, mb: 1.5 },
                        'p': { mb: 2 },
                        'ul, ol': { pl: 5, mb: 2 },
                        'table': { width: '100%', borderCollapse: 'collapse', my: 3, fontSize: 'xs' },
                        'th, td': { border: '1px solid #E2E8F0', p: 1.5, textAlign: 'left' },
                        'th': { bg: 'gray.100', fontWeight: 'bold' },
                      }}
                    >
                      <Text fontSize="xs" fontWeight="bold" color="gray.700" textTransform="uppercase" mb={2}>
                        Detailed &amp; Comprehensive Evidence
                      </Text>
                      <ReactMarkdown>
                        {normalizeAnalysisText(
                          reportAnalysis.analysis_result.evidence_markdown ||
                            reportAnalysis.analysis_result.report_markdown
                        )}
                      </ReactMarkdown>
                    </Box>
                  )}
                </VStack>
              )}
            </ModalBody>
            <ModalFooter bg="white" borderBottomRadius="md">
              <Button size="sm" onClick={onCloseReport}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default JobsDashboard;
